import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface CataclysmicHistoryPageProps {
}

const CataclysmicHistoryPage = (props: CataclysmicHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_post_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Cataclysmic Age - Chase the Fox`,
        description: "The Cataclysmic Age of Casarok is marked by the War of the Gods and the Great Cataclysm that forever changed the world!",
        keywords: ["casarok", "casarok history", "cataclysmic age", "fantasy timeline", "fantasy world history", "cataclysm fantasy", "great cataclysm"],
        imgSrc: _map_src,
        url: `world/history/cataclysmic-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Post-Cataclysm Map'
                         alt="A map of Casarok after the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Cataclysmic Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(_map_src)}
                             title='Casarok Post-Cataclysm Map'
                             alt="A map of Casarok after the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years 85 BCE - 5 ACE.</em>
                    </p>
                    <p>
                        The Cataclysmic Age of Casarok is when the world was shaken by the Great Cataclysm. Prior to the
                        Great Cataclysm, the land of the world was almost entirely on a single continent. Afterwards,
                        8 continents were formed. The Great Cataclysm is the most devastating event in the history of
                        Casarok with the vast majority of the world's population being killed, and nearly all of
                        written history lost to time. The mythical Frozen Archives, referred to commonly as the Great
                        Library, is said to be the only surviving record of the pre-Cataclysmic world.
                    </p>
                    <p>
                        The Great Cataclysm was a result of the War of the Gods.
                        The <NavLink className="world-link" to="/world/religion/pantheon">pantheon of Casarok</NavLink> took
                        two sides in the war to determine the fate of life on Casarok. One group was formed around
                        Ragnarok, the god of death and destruction, who believed it was time to bring about the
                        Endtimes, the fabled end of life on the planet. The other side was spearheaded by Mother, the
                        goddess of life and creation, who cherished the beings of the world and opposed the wishes of
                        the mad dragon god.
                    </p>
                    <p>
                        On Ragnarok's side was Hazado, Blaze, Jotun, Terranis, Ingal, Hieracine, Mohiga, Kentauros,
                        Yamuna, Cobalt, Aurea, Aster, Chrystalia, and Ferni.
                    </p>
                    <p>
                        On Mother's side was Ptahlemy, Domathair, Shen Ling, Sapphiron, Kine, Stratos, Phoenicia,
                        Lionheart, Aerisa, Caduceus, Hypnos, Aimi, and Voki.
                    </p>
                    <p>
                        Barbaru, the god of thunder and lightning magic, started on Mother's side and defected in the
                        middle of the war. All others were neutral or played minor roles.
                    </p>
                    <h3 className="headline">Notable Events</h3>
                    <ul className="spaced">
                        <li>
                            <em>85 BCE</em>: The leader of the gods, Tianlong, was slain at the hand of his brother
                            Ragnarok, a feat completely unthought of throughout the entire pantheon's history.
                        </li>
                        <li>
                            <em>84 BCE</em>: Ragnarok declared to his followers and the rest of the pantheon his
                            intention to enact the Endtimes. Mother stood opposed to him and rallied support.
                        </li>
                        <li>
                            <em>82 BCE</em>: The Kingdom of Leonida, the largest and once most powerful in the world,
                            collapsed. It left a huge power vacuum in the mortal world of Casarok.
                        </li>
                        <li>
                            <em>80 BCE</em>: The sides of the war had fully formed and the fighting began on Casarok.
                        </li>
                        <li>
                            <em>69 BCE</em>: Aimi sacrifices herself to save her followers and becomes the first of the
                            pantheon to fall in the War of the Gods.
                        </li>
                        <li>
                            <em>67 BCE</em>: <NavLink className="world-link" to="/characters/pyrrhe">Pyrrhe Drake</NavLink> forms
                            the Beasts of the Blade, a mercenary group that fights alongside the remainder of Tianlong's
                            followers to assist Mother in the war.
                        </li>
                        <li>
                            <em>55 BCE</em>: Domathair is slain by the followers of Ragnarok. Her fall is the first
                            powerful blow against Mother's alliance.
                        </li>
                        <li>
                            <em>54 BCE</em>: Tempatrius makes his final appearance on Casarok. It is said that he is
                            going mad as he remains in his Realm attempting to fix the Time Stream and prevent the
                            losses of Tianlong and Domathair.
                        </li>
                        <li>
                            <em>42 BCE</em>: The Beasts of the Blade slay Ferni, the first of their many victories.
                        </li>
                        <li>
                            <em>29 BCE</em>: Ragnarok instructs Jotun to begin Fimbulwinter, which would have started
                            the chain of events for the Endtimes. Jotun refuses the order for several years.
                        </li>
                        <li>
                            <em>17 BCE</em>: The Beasts of the Blade claim their first major victory with the defeat
                            of the goddess of fire, Blaze.
                        </li>
                        <li>
                            <em>5 BCE</em>: The Beasts of the Blade slay Jotun, who had finally attempted to start
                            Fimbulwinter.
                        </li>
                        <li>
                            <em>1 BCE</em>: Terranis battles the Beasts of the Blade deep underground. Before he was
                            struck down by Pyrrhe Drake, he causes the Great Cataclysm.
                        </li>
                        <li>
                            <em>4 ACE</em>: Ragnarok's power reaches its peak. He sends forth an edict to the Beasts of
                            the Blade: Challenge him in the Valley of Fate at the foot of Origin Mountain to end the war
                            once and for all or he will use his power to enact the Endtimes anyway.
                        </li>
                        <li>
                            <em>5 ACE</em>: Pyrrhe Drake leads a team into the Valley of Fate. There, he fights and
                            slays Ragnarok, bringing the War of the Gods to an end.
                        </li>
                    </ul>
                    <h3 className="headline">Whereabouts of the Pantheon after the War</h3>
                    <ul className="spaced">
                        <li>
                            <strong>Deceased</strong>: Tianlong (by Ragnarok), Ragnarok (by Pyrrhe Drake), Blaze (by
                            Beasts of the Blade), Jotun (by Beasts of the Blade), Terranis (by Beasts of the Blade),
                            Ingal, Sapphiron (by Ragnarok's forces), Domathair (by Ragnarok's forces), Hieracine,
                            Lionheart (during an assassination attempt on Mother), Kentauros, Bastille, Yamuna,
                            Aimi (while defending her followers), Aster, Chrystalia
                        </li>
                        <li>
                            <strong>Deceased, but remains in another form</strong>: Shen Ling (spiritual), Kine
                            (spiritual), Voki (spiritual; remains in his Realm), Ferni (skeletal ferryman in his Realm)
                        </li>
                        <li>
                            <strong>Gravely wounded</strong>: Galaxia (fled Casarok; considered alive), Barbaru
                            (considered deceased), Cobalt (deep underground; unknown if alive or deceased), Hypnos
                            (asleep deep in the ocean)
                        </li>
                        <li>
                            <strong>Alive</strong>: Tempatrius (does not leave his Realm), Mother (does not leave her
                            Realm), Ptahlemy (last seen in Recovery Age), Hazado (last seen in Recovery Age), Volper
                            (whereabouts unknown), Maita (whereabouts unknown)
                        </li>
                        <li>
                            <strong>No information</strong>: Stratos, Phoenicia, Mohiga, Aerisa, Caduceus, Lyre,
                            Quetzen
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default CataclysmicHistoryPage;